/**
 * Click upgrades & special upgrades data
 * Headless data - no GUI
 */

export interface ClickUpgradeDef {
  index: number;
  nameKey: string;
  cost: number;
  /** For flat upgrades: IP added per tap. For CPS-based: percentage of CPS added per tap */
  flatBonus?: number;
  cpsPercent?: number;
  /** Critical chance bonus (additive, e.g. 0.05 = +5%) */
  critChance?: number;
  /** Critical multiplier bonus (additive, e.g. 1 = +x1) */
  critMult?: number;
}

export const CLICK_UPGRADES: ClickUpgradeDef[] = [
  { index: 0, nameKey: 'click_basic_training', cost: 100, flatBonus: 1 },
  { index: 1, nameKey: 'click_combat_training', cost: 1000, flatBonus: 5 },
  { index: 2, nameKey: 'click_psychic_awaken', cost: 10000, flatBonus: 25 },
  { index: 3, nameKey: 'click_transform', cost: 50000, flatBonus: 100 },
  { index: 4, nameKey: 'click_telepathy', cost: 250000, flatBonus: 500 },
  { index: 5, nameKey: 'click_critical_eye', cost: 500000, critChance: 0.05 },
  { index: 6, nameKey: 'click_dimension_sense', cost: 1000000, flatBonus: 2500 },
  { index: 7, nameKey: 'click_galaxy_field', cost: 5000000, flatBonus: 10000 },
  { index: 8, nameKey: 'click_lethal_strike', cost: 15000000, critMult: 2 },
  { index: 9, nameKey: 'click_absolute', cost: 50000000, cpsPercent: 1 },
  { index: 10, nameKey: 'click_planet_destroyer', cost: 500000000, cpsPercent: 5 },
  { index: 11, nameKey: 'click_galaxy_finger', cost: 10000000000, cpsPercent: 10 },
];

export interface SpecialUpgradeDef {
  id: string;
  nameKey: string;
  descKey: string;
  cost: number;
  condition: SpecialUpgradeCondition;
  effect: SpecialUpgradeEffect;
}

export type SpecialUpgradeCondition =
  | { type: 'producers'; requirements: { producerId: string; count: number }[] }
  | { type: 'combo'; maxCombo: number }
  | { type: 'playtime'; hours: number }
  | { type: 'totalProducers'; count: number }
;

export type SpecialUpgradeEffect =
  | { type: 'producerMultiplier'; producerIds: string[]; multiplier: number }
  | { type: 'allProducerMultiplier'; multiplier: number }
  | { type: 'offlineBonus'; percent: number }
  | { type: 'goldenEventFrequency'; percent: number }
  | { type: 'clickMultiplier'; multiplier: number }
  | { type: 'allCpsMultiplier'; multiplier: number }
  | { type: 'comboCapMultiplier'; multiplier: number }
  | { type: 'offlineCapHours'; hours: number }
  | { type: 'infiltrationMultiplier'; multiplier: number };

export const SPECIAL_UPGRADES: SpecialUpgradeDef[] = [
  {
    id: 'infoNetwork',
    nameKey: 'special_info_network',
    descKey: 'special_info_network_desc',
    cost: 5000,
    condition: { type: 'producers', requirements: [{ producerId: 'informant', count: 10 }, { producerId: 'hacker', count: 10 }] },
    effect: { type: 'producerMultiplier', producerIds: ['informant', 'hacker'], multiplier: 2 },
  },
  {
    id: 'perfectDisguise',
    nameKey: 'special_perfect_disguise',
    descKey: 'special_perfect_disguise_desc',
    cost: 25000,
    condition: { type: 'producers', requirements: [{ producerId: 'disguiser', count: 25 }] },
    effect: { type: 'allProducerMultiplier', multiplier: 1.5 },
  },
  {
    id: 'massBrainwash',
    nameKey: 'special_mass_brainwash',
    descKey: 'special_mass_brainwash_desc',
    cost: 500000,
    condition: { type: 'producers', requirements: [{ producerId: 'mindController', count: 25 }, { producerId: 'mediaCell', count: 10 }] },
    effect: { type: 'producerMultiplier', producerIds: ['mindController', 'mediaCell'], multiplier: 3 },
  },
  {
    id: 'shadowNetwork',
    nameKey: 'special_shadow_network',
    descKey: 'special_shadow_network_desc',
    cost: 2000000,
    condition: { type: 'producers', requirements: [{ producerId: 'shadowGovt', count: 25 }] },
    effect: { type: 'offlineBonus', percent: 25 },
  },
  {
    id: 'undergroundEmpire',
    nameKey: 'special_underground_empire',
    descKey: 'special_underground_empire_desc',
    cost: 20000000,
    condition: { type: 'producers', requirements: [{ producerId: 'deepBase', count: 25 }] },
    effect: { type: 'allProducerMultiplier', multiplier: 2 },
  },
  {
    id: 'orbitalSurveillance',
    nameKey: 'special_orbital_surveillance',
    descKey: 'special_orbital_surveillance_desc',
    cost: 500000000,
    condition: { type: 'producers', requirements: [{ producerId: 'orbitalStation', count: 10 }] },
    effect: { type: 'goldenEventFrequency', percent: 50 },
  },
  {
    id: 'dimensionSupplyLine',
    nameKey: 'special_dimension_supply',
    descKey: 'special_dimension_supply_desc',
    cost: 50000000000,
    condition: { type: 'producers', requirements: [{ producerId: 'dimensionGate', count: 10 }] },
    effect: { type: 'clickMultiplier', multiplier: 5 },
  },
  {
    id: 'galacticAlliance',
    nameKey: 'special_galactic_alliance',
    descKey: 'special_galactic_alliance_desc',
    cost: 100000000000000,
    condition: { type: 'producers', requirements: [{ producerId: 'planetController', count: 1 }, { producerId: 'galacticBeacon', count: 1 }] },
    effect: { type: 'allCpsMultiplier', multiplier: 10 },
  },
  {
    id: 'comboMaster',
    nameKey: 'special_combo_master',
    descKey: 'special_combo_master_desc',
    cost: 100000,
    condition: { type: 'combo', maxCombo: 100 },
    effect: { type: 'comboCapMultiplier', multiplier: 3 },
  },
  {
    id: 'timeWarp',
    nameKey: 'special_time_warp',
    descKey: 'special_time_warp_desc',
    cost: 10000000,
    condition: { type: 'playtime', hours: 24 },
    effect: { type: 'offlineCapHours', hours: 12 },
  },
  {
    id: 'agentSynergy',
    nameKey: 'special_agent_synergy',
    descKey: 'special_agent_synergy_desc',
    cost: 50000,
    condition: { type: 'totalProducers', count: 100 },
    effect: { type: 'allCpsMultiplier', multiplier: 1.5 },
  },
  {
    id: 'massiveInfiltration',
    nameKey: 'special_massive_infiltration',
    descKey: 'special_massive_infiltration_desc',
    cost: 5000000000,
    condition: { type: 'totalProducers', count: 500 },
    effect: { type: 'allCpsMultiplier', multiplier: 2 },
  },
  {
    id: 'spyMaster',
    nameKey: 'special_spy_master',
    descKey: 'special_spy_master_desc',
    cost: 500000000,
    condition: { type: 'producers', requirements: [{ producerId: 'hacker', count: 50 }, { producerId: 'disguiser', count: 50 }] },
    effect: { type: 'infiltrationMultiplier', multiplier: 2 },
  },
];
